import { faExternalLink, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from '@tanstack/react-router'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MenuListProps, components } from 'react-select'

import {
  Ingredient,
  IngredientUnit,
  ScaledRecipeIngredient,
  SearchIngredient,
  TempRecipeIngredient,
} from '@/api/types'
import { Button } from '@/components/Button'
import { Input } from '@/components/Input'
import { SelectRef } from '@/components/Select'
import { getIngredientEnergy } from '@/features/meal-recommendations/utils'
import { roundNumber } from '@/utils'
import './IngredientsEditorItem.css'
import NewServingSizeModal from './NewServingSizeModal'

type Props = {
  recipeIngredient: TempRecipeIngredient
  language?: string
  deleteRecIngredient: (ingId: number) => void
  updateRecIngredient: (recIng: TempRecipeIngredient) => void
  isQuantityFocused: boolean
  isDisabled?: boolean
  onBlur: () => void
}

function getTranslation(
  ingredient: Ingredient | SearchIngredient,
  languageCode?: string,
): string | undefined {
  if (!ingredient.nameTranslations || !languageCode) return ingredient.name

  const translation = ingredient.nameTranslations.find((t) => t.languageCode === languageCode)

  if (!translation && languageCode === 'en') return ingredient.englishName

  return translation?.translation || ingredient.name
}

export default function IngredientsEditorItem({
  recipeIngredient,
  language,
  deleteRecIngredient,
  updateRecIngredient,
  isQuantityFocused,
  isDisabled,
  onBlur,
}: Props) {
  const { t } = useTranslation()
  const { ingredient, quantity } = recipeIngredient
  const [unitOptions, setUnitOptions] = useState(recipeIngredient.ingredient.units ?? [])
  const [isOpen, setOpen] = useState(false)
  const selectRef = useRef<any>()

  const handleUpdateQuantity = (ev: React.ChangeEvent<HTMLInputElement>) => {
    updateRecIngredient({
      ...recipeIngredient,
      quantity: parseFloat(ev.currentTarget.value),
    })
  }

  const handleUpdateUnit = (unit: IngredientUnit) => {
    updateRecIngredient({
      ...recipeIngredient,
      unit: unit.name === 'GRAMS' ? undefined : unit,
    })
  }

  const handleSetSelectValue = (unit: IngredientUnit) => {
    selectRef.current.setValue(unit)
  }

  const optionGrams = { name: 'GRAMS', quantityInGrams: 1 }

  const unitValue = recipeIngredient.unit ?? optionGrams

  return (
    <div className="IngredientsEditorItem">
      <div className="IngredientsEditorItem-name">
        <div className="IngredientsEditorItem-value">
          {getTranslation(ingredient, language)}

          {ingredient.ingredientGroup && (
            <div className="IngredientsEditorItem-displayName">
              <Link
                to="/ingredients/displaynames/$ingId"
                params={{
                  ingId: ingredient.ingredientGroup.idIngredientGroup?.toString() as string,
                }}>
                {ingredient.ingredientGroup.name}
              </Link>
            </div>
          )}
        </div>
        <Link to="/ingredients/fcdb/$ingId" params={{ ingId: ingredient.idIngredient.toString() }}>
          <FontAwesomeIcon icon={faExternalLink} color="var(--highlight-background)" />
        </Link>
      </div>

      <div className="IngredientsEditor-unit">
        <SelectRef
          isDisabled={isDisabled}
          ref={selectRef}
          options={[optionGrams, ...unitOptions]}
          defaultValue={unitValue}
          menuPortalTarget={document.body}
          components={{
            IndicatorSeparator: null,
            MenuList: (props) => <MenuList setOpen={setOpen} {...props} />,
          }}
          getOptionLabel={(option) =>
            option.name === 'GRAMS'
              ? t(`measurementUnits.GRAMS`)
              : t(`measurementUnits.${option.name}` as any) + ` (${option.quantityInGrams}g)`
          }
          getOptionValue={(option) => option.name}
          onChange={handleUpdateUnit}
        />
      </div>

      <div className="IngredientsEditorItem-quantity">
        <Input
          disabled={isDisabled}
          value={quantity}
          type="number"
          step={0.01}
          required
          min={0}
          autoFocus={isQuantityFocused}
          onChange={handleUpdateQuantity}
          onBlur={onBlur}
          onFocus={(ev) => isQuantityFocused && ev.target.select()}
        />
      </div>

      <div className="IngredientsEditorItem-kcal">
        {Number.isNaN(quantity) ? (
          '-'
        ) : (
          <div>
            {roundNumber(getIngredientEnergy(recipeIngredient as ScaledRecipeIngredient))}kcal
          </div>
        )}
      </div>
      {!isDisabled && (
        <Button variant="icon" onPress={() => deleteRecIngredient(ingredient.idIngredient)}>
          <FontAwesomeIcon icon={faTrashAlt} color="var(--invalid-color)" />
        </Button>
      )}

      <NewServingSizeModal
        isOpen={isOpen}
        setOpen={setOpen}
        onUpdateUnits={setUnitOptions}
        onUpdateUnit={handleSetSelectValue}
        units={recipeIngredient.ingredient.units}
        idIngredient={ingredient.idIngredient}
      />
    </div>
  )
}

type TMenuListProps = {
  setOpen: (state: boolean) => void
} & MenuListProps<any>

const MenuList = ({ setOpen, ...props }: TMenuListProps) => {
  const { t } = useTranslation()

  return (
    <components.MenuList {...props}>
      {props.children}

      <div onClick={() => setOpen(true)} className="IngredientsEditorItem-selectNewItem">
        {t('ingredient.newUnit')}
      </div>
    </components.MenuList>
  )
}
